/**
 * Plain-language access summaries for the Team feature.
 *
 * Overhaul decision: NO "% access" progress bars. Access is described in words,
 * both in the member list (short label) and in the modal footer (a full
 * live-summary sentence). Percentages were misleading (columns meant different
 * things per row) so they are gone.
 */
import {
	TEAM_PERMISSION_ROWS,
	createModeratorPreset,
	levelFromPermissions,
	rowIsBinary,
	scopeModeFromIds,
} from '@/Utils/teamPermissionRows'

export function resolveTeamMember(props) {
	return props?.member || props?.params?.data || {}
}

export function teamRoleKey(role) {
	return String(role || '').toLowerCase()
}

export function teamRoleLabel(role) {
	const key = teamRoleKey(role)
	if (!key) return ''
	return key.charAt(0).toUpperCase() + key.slice(1)
}

// Map a role → a Badge `type` that actually exists in src/components/Badge.vue.
// The old `role-*` values fell through to Badge's default grey (no `badge-role-*`
// classes exist), so every role rendered identically. These map to distinct,
// supported colors: owner=blue, admin=purple, moderator/custom=neutral grey.
export function teamRoleBadgeType(role) {
	const key = teamRoleKey(role)
	if (key === 'owner') return 'blue'
	if (key === 'admin') return 'purple'
	// Moderator (the backend default) is the third distinct color. Not green —
	// that collides with the green "Active" status pill on every active-moderator
	// row — and not the old flat grey that read as "no badge". `audio` is a solid
	// magenta (white text) that's clearly distinct from Owner (blue), Admin
	// (purple), and every status color. Unknown roles fall here too, since a row
	// with no explicit role IS a moderator.
	return 'audio'
}

/**
 * First real (letter/number) character of a string, upper-cased. Skips leading
 * spaces, punctuation, and invisible characters (zero-width spaces etc. that
 * `String.trim()` leaves behind) which were rendering some avatars blank.
 */
function initialFrom(value) {
	if (value == null) return ''
	const match = String(value).match(/[\p{L}\p{N}]/u)
	return match ? match[0].toUpperCase() : ''
}

/**
 * Avatar initial with a robust fallback chain so EVERY row shows a glyph:
 *   name → firstName → lastName → email → "?".
 * The owner object is assembled differently from members/invites (see
 * useTeamMembers) and can lack the field a naive `name[0]` relied on.
 */
export function getTeamMemberInitial(member) {
	return (
		initialFrom(member?.name)
		|| initialFrom(member?.firstName)
		|| initialFrom(member?.lastName)
		|| initialFrom(member?.email)
		|| '?'
	)
}

export function getInviteTimestamp(member) {
	return member?.createdAt || member?.created || member?.created_at || member?.invitedAt || member?.dateCreated || null
}

/** Rows a member's access should be measured against (defaults to the full set). */
function rowsFor(ctx) {
	return (ctx && ctx.rows && ctx.rows.length) ? ctx.rows : TEAM_PERMISSION_ROWS
}

/** Count of product areas the member can reach (level !== none / switch on). */
export function enabledAreaCount(permissions, rows) {
	return (rows || TEAM_PERMISSION_ROWS)
		.filter((row) => levelFromPermissions(permissions, row) !== 'none').length
}

/** Are ALL product areas at their maximum (full / on) with unrestricted scope? */
function isEverything(permissions, rows) {
	const list = rows || TEAM_PERMISSION_ROWS
	const allMax = list.every((row) => {
		const level = levelFromPermissions(permissions, row)
		if (rowIsBinary(row)) return level !== 'none'
		return level === 'full'
	})
	if (!allMax) return false
	const liveAll = scopeModeFromIds(permissions?.liveStreamAccessIds) === 'all'
	const vodAll = scopeModeFromIds(permissions?.vodStreamAccessIds) === 'all'
	return liveAll && vodAll
}

/** Any scoped area limited to specific items? (for the "· specific content" note) */
function hasLimitedScope(permissions) {
	return (
		scopeModeFromIds(permissions?.liveStreamAccessIds) === 'specific' ||
		scopeModeFromIds(permissions?.vodStreamAccessIds) === 'specific'
	)
}

/**
 * Short access label for the member list (words, never a bar/percentage).
 * @returns {{ label: string, tone: 'full'|'none'|'partial' }}
 */
export function describeAccess(member, ctx = {}) {
	const permissions = member?.permissions
	const role = teamRoleKey(member?.role || permissions?.role)
	const isPending = member?.status === 'pending'
	const isEnabled = member?.enabled !== false && permissions?.isEnabled !== false
	const rows = rowsFor(ctx)

	if (!isPending && !isEnabled) {
		return { label: 'No access', tone: 'none' }
	}
	if (role === 'owner' || role === 'admin' || !permissions) {
		return { label: 'Full access', tone: 'full' }
	}
	const count = enabledAreaCount(permissions, rows)
	if (count === 0) return { label: 'No access', tone: 'none' }
	if (isEverything(permissions, rows)) return { label: 'Full access', tone: 'full' }

	const areas = `${count} area${count === 1 ? '' : 's'}`
	const label = hasLimitedScope(permissions) ? `${areas} · specific content` : areas
	return { label, tone: 'partial' }
}

/** Human scope note for a scoped row limited to specific items, else null. */
function rowScopeNote(row, permissions) {
	if (!row?.scope || !row?.accessKey) return null
	if (scopeModeFromIds(permissions?.[row.accessKey]) !== 'specific') return null
	if (row.scope === 'live') return 'specific streams'
	if (row.scope === 'vod') return 'specific folders'
	return 'specific items'
}

/**
 * Rich access summary for the member list. Unlike `describeAccess` (which
 * collapses to an opaque "N areas"), this returns the concrete area names so the
 * UI can show the first couple inline with a "+N" and list EVERY area — with
 * scope — in a hover tooltip.
 *
 * @returns {{
 *   label: string,                 // short inline text ("Live Stream, Paywall +7")
 *   tone: 'full'|'none'|'partial',
 *   areas: {name: string, scope: string|null}[], // full list for the tooltip
 * }}
 */
export function describeAccessDetail(member, ctx = {}) {
	const permissions = member?.permissions
	const role = teamRoleKey(member?.role || permissions?.role)
	const isPending = member?.status === 'pending'
	const isEnabled = member?.enabled !== false && permissions?.isEnabled !== false
	const rows = rowsFor(ctx)

	if (!isPending && !isEnabled) {
		return { label: 'No access', tone: 'none', areas: [] }
	}
	if (role === 'owner' || role === 'admin' || !permissions) {
		return { label: 'Full access', tone: 'full', areas: [] }
	}

	const areas = rows
		.filter((row) => levelFromPermissions(permissions, row) !== 'none')
		.map((row) => ({ name: row.label, scope: rowScopeNote(row, permissions) }))

	if (areas.length === 0) {
		return { label: 'No access', tone: 'none', areas: [] }
	}

	if (isEverything(permissions, rows)) {
		return { label: 'Full access', tone: 'full', areas }
	}

	const names = areas.map((a) => a.name)
	const shown = names.slice(0, 2)
	const remaining = names.length - shown.length
	let label = shown.join(', ')
	if (remaining > 0) label += ` +${remaining}`
	return { label, tone: 'partial', areas }
}

/**
 * Full live-summary sentence for the modal footer.
 */
export function accessSummarySentence(permissions, ctx = {}) {
	const role = teamRoleKey(ctx.role || permissions?.role)
	const rows = rowsFor(ctx)
	if (role === 'admin') {
		return 'Admins can manage everything, including billing and team settings.'
	}
	const enabled = rows
		.map((row) => ({ row, level: levelFromPermissions(permissions, row) }))
		.filter((entry) => entry.level !== 'none')

	if (!enabled.length) {
		return 'No access yet — turn on at least one area below.'
	}

	const verbFor = (row, level) => {
		if (rowIsBinary(row)) return `use ${row.label}`
		// "view Live Stream" overstates a chat-restricted moderator's access.
		if (row.id === 'live' && level === 'view' && permissions?.liveStreamChatModerationOnly) {
			return `moderate chat on ${row.label}`
		}
		if (level === 'view') return `view ${row.label}`
		if (level === 'edit') return `manage ${row.label}`
		return `manage and delete ${row.label}`
	}

	const phrases = enabled.slice(0, 3).map(({ row, level }) => verbFor(row, level))
	let list = phrases.join(', ')
	const remaining = enabled.length - phrases.length
	if (remaining > 0) list += `, and ${remaining} more`

	let sentence = `Can ${list}.`
	if (hasLimitedScope(permissions)) sentence += ' Limited to specific content.'
	return sentence
}

/**
 * Whether a moderator still matches the conservative default preset. When it
 * doesn't, the UI shows a derived "Custom access" label (backend role stays
 * `moderator`).
 */
export function isPresetModerator(permissions, rows) {
	if (!permissions) return true
	const preset = createModeratorPreset()
	const list = rows && rows.length ? rows : TEAM_PERMISSION_ROWS
	for (const row of list) {
		if (levelFromPermissions(permissions, row) !== levelFromPermissions(preset, row)) {
			return false
		}
	}
	if (!!permissions.teamInviteUsers !== !!preset.teamInviteUsers) return false
	if (scopeModeFromIds(permissions.liveStreamAccessIds) !== 'all') return false
	if (scopeModeFromIds(permissions.vodStreamAccessIds) !== 'all') return false
	return true
}

/**
 * Role label to DISPLAY. The badge is the ACTUAL role only — Owner / Admin /
 * Moderator. Whether a moderator's permissions diverge from the preset is
 * surfaced as a quiet "Custom" hint in the ACCESS column (see
 * `describeAccessDetail`), never as the role, so the badge stays truthful.
 */
export function deriveRoleLabel(member) {
	const role = teamRoleKey(member?.role || member?.permissions?.role)
	if (role === 'owner') return 'Owner'
	if (role === 'admin') return 'Admin'
	// Moderator is the default: a pending invite (or any row) with no explicit
	// role still gets a Moderator badge instead of an empty one, so EVERY row —
	// owner, active member, pending invite — always renders a role badge.
	return 'Moderator'
}
